import type { Column } from "@tanstack/react-table";
import {
  ArrowUpIcon,
  ArrowDownIcon,
  EyeOffIcon,
  PinIcon,
  PlusIcon,
  Trash2Icon,
  MoveLeftIcon,
  MoveRightIcon,
  MoreHorizontalIcon,
  CheckIcon,
} from "lucide-react";

import { Button } from "src/components/Button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "src/primitives/DropdownMenu";

import type { ColumnMoreAction, DataTableColumnMeta } from "../types";

interface HeaderCellMenuProps<TData> {
  column: Column<TData, unknown>;
  enableAddColumn?: boolean;
  enableColumnFreeze?: boolean;
  isColumnPinned?: boolean;
  canMoveLeft?: boolean;
  canMoveRight?: boolean;
  onSort?: (columnId: string, desc: boolean) => void;
  onHideColumn?: (columnId: string) => void;
  onTogglePin?: (columnId: string) => void;
  onAddColumn?: (position: number) => void;
  onDeleteColumn?: (columnId: string) => void;
  onMoveColumn?: (columnId: string, direction: -1 | 1) => void;
  onMoreActionClick?: (actionType: string, columnId: string) => void;
  columnIndex: number;
}

export const HeaderCellMenu = <TData,>({
  column,
  enableAddColumn,
  enableColumnFreeze,
  isColumnPinned,
  canMoveLeft,
  canMoveRight,
  onSort,
  onHideColumn,
  onTogglePin,
  onAddColumn,
  onDeleteColumn,
  onMoveColumn,
  onMoreActionClick,
  columnIndex,
}: HeaderCellMenuProps<TData>) => {
  const meta = column.columnDef.meta as DataTableColumnMeta | undefined;
  const isSortable = column.getCanSort();
  const isHidable = column.getCanHide();
  const enableDelete = meta?.enableDelete === true;
  const moreActions: ColumnMoreAction[] = meta?.moreActions ?? [];
  const currentSort = column.getIsSorted();

  const hasAnyAction =
    isSortable ||
    isHidable ||
    enableAddColumn ||
    (enableColumnFreeze && column.getCanPin()) ||
    enableDelete ||
    (onMoveColumn && (canMoveLeft || canMoveRight)) ||
    moreActions.length > 0;

  if (!hasAnyAction) return null;

  return (
    <div onClick={e => e.stopPropagation()}>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            icon={MoreHorizontalIcon}
            data-testid="column-menu-button"
          />
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-48">
          {isSortable && (
            <>
              <DropdownMenuItem
                data-testid="ascending-column-menu-button"
                onClick={() => onSort?.(column.id, false)}
              >
                <ArrowUpIcon className="mr-2 size-4" />
                Ascending
                {currentSort === "asc" && (
                  <CheckIcon className="ml-auto size-4 text-primary" />
                )}
              </DropdownMenuItem>
              <DropdownMenuItem
                data-testid="descending-column-menu-button"
                onClick={() => onSort?.(column.id, true)}
              >
                <ArrowDownIcon className="mr-2 size-4" />
                Descending
                {currentSort === "desc" && (
                  <CheckIcon className="ml-auto size-4 text-primary" />
                )}
              </DropdownMenuItem>
            </>
          )}
          {enableAddColumn && (
            <>
              <DropdownMenuItem
                data-testid="insert-right-column-menu-button"
                onClick={() => onAddColumn?.(columnIndex + 1)}
              >
                <PlusIcon className="mr-2 size-4" />
                Insert column right
              </DropdownMenuItem>
              <DropdownMenuItem
                data-testid="insert-left-column-menu-button"
                onClick={() => onAddColumn?.(columnIndex)}
              >
                <PlusIcon className="mr-2 size-4" />
                Insert column left
              </DropdownMenuItem>
            </>
          )}
          {isHidable && onHideColumn && (
            <>
              <DropdownMenuItem
                data-testid="hide-column-menu-button"
                onClick={() => onHideColumn(column.id)}
              >
                <EyeOffIcon className="mr-2 size-4" />
                Hide column
              </DropdownMenuItem>
            </>
          )}
          {enableDelete && onDeleteColumn && (
            <DropdownMenuItem
              data-testid="delete-column-menu-button"
              onClick={() => onDeleteColumn(column.id)}
            >
              <Trash2Icon className="mr-2 size-4" />
              Delete column
            </DropdownMenuItem>
          )}
          {enableColumnFreeze && onTogglePin && column.getCanPin() && (
            <>
              <DropdownMenuItem
                data-testid="freeze-unfreeze-column-menu-button"
                onClick={() => onTogglePin(column.id)}
              >
                <PinIcon className="mr-2 size-4" />
                {isColumnPinned ? "Unfreeze column" : "Freeze column"}
              </DropdownMenuItem>
            </>
          )}
          {onMoveColumn && (canMoveLeft || canMoveRight) && (
            <>
              {canMoveLeft && (
                <DropdownMenuItem
                  data-testid="move-column-left-menu-button"
                  onClick={() => onMoveColumn(column.id, -1)}
                >
                  <MoveLeftIcon className="mr-2 size-4" />
                  Move column left
                </DropdownMenuItem>
              )}
              {canMoveRight && (
                <DropdownMenuItem
                  data-testid="move-column-right-menu-button"
                  onClick={() => onMoveColumn(column.id, 1)}
                >
                  <MoveRightIcon className="mr-2 size-4" />
                  Move column right
                </DropdownMenuItem>
              )}
            </>
          )}
          {moreActions.length > 0 && (
            <>
              {moreActions.map(action => {
                const ActionIcon = action.icon;

                return (
                  <DropdownMenuItem
                    key={action.type}
                    data-testid={`${action.type}-column-menu-button`}
                    onClick={() => onMoreActionClick?.(action.type, column.id)}
                  >
                    {ActionIcon && <ActionIcon className="mr-2 size-4" />}
                    {action.label}
                  </DropdownMenuItem>
                );
              })}
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};
